import dayjs from 'dayjs';

import { isBeforeDay } from '~libs/dayjs';

import { LookupStatus } from '~components/LookupStatusBadge';

import { CarePlan, CarePlanSummaryStatus, NeedPriority } from '~clients/models';

// Care Plan enumerations. `priority` is a frontend-hardcoded enum (lowercase
// snake_case slug); the map below is the single source of truth for its display
// labels. Goal status/approach-type and review type/trigger-type are lookup FKs —
// their labels come from the lookup endpoints, not from this file.

export const NEED_PRIORITY_LABELS: Record<NeedPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** Build ordered {value,label} select options from a label map (insertion order). */
export const toOptions = <T extends string>(labels: Record<T, string>): { value: T; label: string }[] =>
  (Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] }));

// Client-list summary status. The `care_plan_status` returned by
// GET /v2/clients/care-plans is one of active | overdue | none.
export const CARE_PLAN_SUMMARY_STATUS_LABELS: Record<CarePlanSummaryStatus, string> = {
  active: 'Active',
  overdue: 'Overdue',
  expired: 'Expired',
  none: 'None active',
};

// LookupStatus objects consumed by the shared `LookupStatusBadge` component (the
// app-wide single source of truth for status-badge rendering). Colours: active green,
// overdue red, none grey.
export const CARE_PLAN_SUMMARY_STATUS_BADGE: Record<CarePlanSummaryStatus, LookupStatus> = {
  active: { tag: 'active', name: CARE_PLAN_SUMMARY_STATUS_LABELS.active, colour: '#4CAF50' },
  overdue: { tag: 'overdue', name: CARE_PLAN_SUMMARY_STATUS_LABELS.overdue, colour: '#E53935' },
  // Dark grey, matching the per-plan expired badge (CARE_PLAN_STATUS_BADGE.expired).
  expired: { tag: 'expired', name: CARE_PLAN_SUMMARY_STATUS_LABELS.expired, colour: '#616161' },
  none: { tag: 'none', name: CARE_PLAN_SUMMARY_STATUS_LABELS.none, colour: '#9E9E9E' },
};

// Per-plan status. Like the client-level summary status, this is computed — never
// stored (there is no status column on client_care_plans). Derived entirely on the
// frontend from activated_at / end_date / review_due_date.
export type CarePlanStatus = 'draft' | 'active' | 'overdue' | 'expired';

export const CARE_PLAN_STATUS_LABELS: Record<CarePlanStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  overdue: 'Overdue',
  expired: 'Expired',
};

export const CARE_PLAN_STATUS_BADGE: Record<CarePlanStatus, LookupStatus> = {
  draft: { tag: 'draft', name: CARE_PLAN_STATUS_LABELS.draft, colour: '#9E9E9E' },
  active: { tag: 'active', name: CARE_PLAN_STATUS_LABELS.active, colour: '#4CAF50' },
  overdue: { tag: 'overdue', name: CARE_PLAN_STATUS_LABELS.overdue, colour: '#E53935' },
  expired: { tag: 'expired', name: CARE_PLAN_STATUS_LABELS.expired, colour: '#616161' },
};

// "Linked" pill shared by the package-schedule and SAH-budget overview sections.
// bs-success green (198754), matching the Direct Debits mandate-status badge;
// layout via the .care-plan-linked-badge style.
export const CARE_PLAN_LINKED_BADGE: LookupStatus = { tag: 'linked', name: 'Linked', colour: '198754' };

/** Days ahead of the review due date at which the Overview tab highlights it amber. */
export const REVIEW_DUE_SOON_DAYS = 30;

/**
 * Whether the review due date is approaching — today or within REVIEW_DUE_SOON_DAYS.
 * A past due date is not "soon": that state is already surfaced as the overdue status.
 */
export const isReviewDueSoon = (reviewDueDate?: string | null): boolean => {
  if (!reviewDueDate) return false;
  if (isBeforeDay(reviewDueDate, dayjs())) return false;
  return dayjs(reviewDueDate).diff(dayjs(), 'day') <= REVIEW_DUE_SOON_DAYS;
};

/** Colour state for the Reviews-tab next-review banner. */
export type ReviewDueState = 'overdue' | 'soon' | 'future' | 'none';

/** Overdue (past) → soon (≤ REVIEW_DUE_SOON_DAYS) → future; none when unset. */
export const reviewDueState = (reviewDueDate?: string | null): ReviewDueState => {
  if (!reviewDueDate) return 'none';
  if (isBeforeDay(reviewDueDate, dayjs())) return 'overdue';
  return isReviewDueSoon(reviewDueDate) ? 'soon' : 'future';
};

/**
 * Derive the display status of a single care plan. Precedence:
 * draft (never activated) → expired (past end date) → overdue (review due) → active.
 * An unactivated plan is draft regardless of its dates.
 */
export const deriveCarePlanStatus = (
  plan: Pick<CarePlan, 'activated_at' | 'end_date' | 'review_due_date'>,
): CarePlanStatus => {
  if (!plan.activated_at) return 'draft';
  if (plan.end_date && isBeforeDay(plan.end_date, dayjs())) return 'expired';
  if (plan.review_due_date && isBeforeDay(plan.review_due_date, dayjs())) return 'overdue';
  return 'active';
};
